'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import { GenreDto } from '@cineamo/api/models/movie/GenreDto.types';

import {
    getMovieAccessibility,
    hasMovieAccessibilityFeatures,
    MovieWithAccessibility
} from '@cineamo/api/helpers/movie-helper';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { formatDuration } from '@cineamo/ui/src/helpers/showing-helper';

import { GretaIconVariant } from '@cineamo/ui/src/icons/ic-greta';
import DotSeparatedLabel from '@cineamo/ui/src/label/DotSeparatedLabel';

import GretaLabel from '../movie/GretaLabel';

/**
 * Default typography of the title and the meta labels. These are *replaced* by
 * `classNameTitle` / `classNameMetaLabel` rather than merged with them: `cn()` cannot reliably
 * de-duplicate our custom `text-…` scale (tailwind-merge only knows the stock font sizes), so
 * emitting both a default and an override size would leave the winner up to CSS order.
 */
const TITLE_CLASS_NAME = 'text-headline-18 lg:text-headline-20';
const META_LABEL_CLASS_NAME = 'text-body-bold-12 lg:text-body-bold-14';

/** Genres beyond the third are dropped — the meta row is a single line in most layouts. */
const MAX_GENRES = 3;

/**
 * Everything `ContentTitle` reads off the movie, as a structural type so both a movie DTO (v1/v2)
 * and an app's flattened movie view (e.g. the cinema-website's `CachedContentData`) fit without a
 * mapping step. `releaseYear` wins over `releaseDate` because the flattened views resolve the
 * locale-aware year up front.
 */
export type ContentTitleMovie = MovieWithAccessibility & {
    releaseYear?: string | null;
    releaseDate?: string | null;
    runtime?: number | null;
    genres?: GenreDto[] | null;
};

export type ContentTitleProps = {
    /** Content/movie title text. */
    title: string;
    /** Movie the title belongs to — the GRETA badge and the meta labels are derived from it. */
    movie?: ContentTitleMovie | null;
    /**
     * Renders the title text. Use it to wrap the title in the app's typed `Link` or in a heading
     * tag — a shared package cannot import an app's router. Spread the passed `className` onto the
     * element you return so the shared typography stays intact. Defaults to a plain `div`.
     */
    renderTitle?: (params: { className: string; children: React.ReactNode }) => React.ReactNode;
    /**
     * Age-rating badge (e.g. the FSK icon). Passed in as a node rather than derived, because the
     * two apps resolve the rating differently (a query hook in the web app, server-side in the
     * cinema-website) and wrap it in their own info modal and tracking.
     */
    ageRatingBadge?: React.ReactNode;
    /**
     * The age rating as text ("Ab 16 Jahren"), shown as the first meta label *instead of*
     * `ageRatingBadge` when there is no badge — countries without an icon set (i.e. anything but DE).
     */
    ageRatingText?: string;
    /** Colourway of the GRETA badge — on a cinema website this comes from the domain configuration. */
    gretaIconVariant?: GretaIconVariant;
    /** Show the movie's release year. */
    showReleaseYear?: boolean;
    /** Show the movie's running time (e.g. "2h 53min"). */
    showMovieDuration?: boolean;
    /** Show up to {@link MAX_GENRES} genres as one comma-separated label. */
    showGenres?: boolean;
    /** Running time to fall back to when the movie itself has none (e.g. a content's own duration). */
    fallbackRuntimeMinutes?: number;
    /** Rendered right of the title (e.g. a "new release" badge). */
    titleBadges?: React.ReactNode;
    /** Rendered at the far right of the title row (e.g. a share button). */
    titleActions?: React.ReactNode;
    /** Keep the meta labels on one line and ellipsize them instead of wrapping to a second line. */
    metaSingleLine?: boolean;
    className?: string;
    /** Replaces the default title typography (`text-headline-18 lg:text-headline-20`). */
    classNameTitle?: string;
    /** Added to the meta-label container. */
    classNameMeta?: string;
    /** Replaces the default meta-label typography (`text-body-bold-12 lg:text-body-bold-14`). */
    classNameMetaLabel?: string;
    styleMeta?: React.CSSProperties;
};

/**
 * Title row of a content/movie (title + optional badges/actions) over a meta row carrying the
 * age-rating badge, the GRETA accessibility badge and the dot-separated release year / running
 * time / genres.
 *
 * Give it the movie and switch the labels on — it derives them itself, GRETA badge included. Only
 * the age-rating badge and the title element stay injectable: the rating is resolved differently per
 * app (a query hook in the web app, server-side in the cinema-website) and a shared package cannot
 * reach an app's typed router.
 */
function ContentTitle(props: ContentTitleProps): React.JSX.Element {
    const { showReleaseYear = true, showMovieDuration = false, showGenres = false, movie } = props;

    const t = useTranslations();

    const releaseYear = movie?.releaseYear || movie?.releaseDate?.substring(0, 4) || undefined;
    const runtimeMinutes = movie?.runtime || props.fallbackRuntimeMinutes;
    const genreLabel = (movie?.genres ?? [])
        .slice(0, MAX_GENRES)
        // t.has(): TMDB keeps adding genres, so fall back to the API's own (untranslated) name
        // instead of rendering a raw translation key
        .map((genre) => (t.has(`genres.tmdb-genre-${genre.id}`) ? t(`genres.tmdb-genre-${genre.id}`) : genre.name))
        .filter(Boolean)
        .join(', ');

    const metaLabels = [
        // takes the badge's place at the start of the row when no icon exists for the country
        props.ageRatingBadge ? undefined : props.ageRatingText,
        showReleaseYear ? releaseYear : undefined,
        showMovieDuration && runtimeMinutes ? formatDuration(runtimeMinutes, t) : undefined,
        showGenres ? genreLabel : undefined
    ].filter((label): label is string => !!label);

    // GretaLabel runs the same check and renders nothing without features; repeated here because
    // the meta row must know up front whether it has anything to show at all.
    const showGretaBadge = hasMovieAccessibilityFeatures(getMovieAccessibility(movie));
    const titleClassName = cn('grow min-w-0', props.classNameTitle ?? TITLE_CLASS_NAME);
    const hasMetaRow = !!props.ageRatingBadge || showGretaBadge || metaLabels.length > 0;

    return (
        <div className={cn('w-full', props.className)}>
            <div className="flex flex-row gap-16 items-center">
                {props.renderTitle ? (
                    props.renderTitle({ className: titleClassName, children: props.title })
                ) : (
                    <div className={titleClassName}>{props.title}</div>
                )}
                {props.titleBadges && <div className="flex flex-row shrink-0 space-x-8">{props.titleBadges}</div>}
                {props.titleActions}
            </div>
            {hasMetaRow && (
                // The badges get their own flex box so they are laid out as block-level boxes: an
                // inline-level badge would sit on the wrapper's baseline, and the line box then
                // reserves descender space below it — `items-center` would centre "icon + phantom
                // space" instead of the icon, leaving the badge sitting above the meta text.
                <div className="flex flex-row items-center gap-10 mt-4">
                    {props.ageRatingBadge && <span className="flex shrink-0 items-center">{props.ageRatingBadge}</span>}
                    {showGretaBadge && (
                        <GretaLabel
                            movie={movie}
                            movieTitle={props.title}
                            variant={props.gretaIconVariant}
                        />
                    )}
                    {metaLabels.length > 0 && (
                        <DotSeparatedLabel
                            className={cn('min-w-0 flex-1', props.metaSingleLine && 'truncate', props.classNameMeta)}
                            classNameLabel={props.classNameMetaLabel ?? META_LABEL_CLASS_NAME}
                            multiline={!props.metaSingleLine}
                            labels={metaLabels}
                            style={props.styleMeta}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

export default ContentTitle;
